import { Alert, AlertIcon, Box, Button, Stack, Text, useDisclosure } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiMessageSquare } from "react-icons/fi";
import {
  SeQuestionDto,
  SeQuestionKind,
  SourcingEventStatus,
  SourcingEventDto,
  useListBidsForEventQuery,
} from "../../../autogen/bff-api";
import { formatDateTime } from "../../../common/components/communication/communicationUtils";
import { isBidDeadlinePassed } from "../supplierRows";
import { BuyerClarificationThread } from "./BuyerClarificationThread";
import { clarificationRuleOf } from "./clarificationRule";
import { ClarificationCounterparty, StartClarificationModal } from "./StartClarificationModal";

export const ClarificationsPanel = ({
  event,
  questions,
  supplierOrganizationId,
}: {
  event: SourcingEventDto;
  questions: SeQuestionDto[];
  supplierOrganizationId?: string;
}) => {
  const { t } = useTranslation();
  const eventId = event.id;
  const startModal = useDisclosure();

  const deadlinePassed = isBidDeadlinePassed(event);
  const cancelled = event.status === SourcingEventStatus.Cancelled;
  const bidDeadline = event.milestoneDates?.bidSubmissionDeadline;
  // FOA § 23-5 reserves the one-to-one dialog for after the deadline; that rule only binds the public flow.
  const dialogWindowOpen = event.eventContext?.type === "Private" || deadlinePassed;

  const { data: bidsData } = useListBidsForEventQuery({ eventId }, { skip: !dialogWindowOpen });
  const counterparties: ClarificationCounterparty[] = (bidsData?.bids ?? [])
    .filter((bid) => !supplierOrganizationId || bid.owner === supplierOrganizationId)
    .map((bid) => ({ organizationId: bid.owner, organizationName: bid.ownerName }))
    .filter(
      (counterparty, index, all) =>
        all.findIndex((c) => c.organizationId === counterparty.organizationId) === index,
    );

  const clarifications = questions
    .filter((question) => question.kind === SeQuestionKind.Clarification)
    .filter((question) => !supplierOrganizationId || question.supplierOrganizationId === supplierOrganizationId)
    .slice()
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  const canStart = dialogWindowOpen && !cancelled && counterparties.length > 0 && !event.entriesAnonymous;
  const rule = clarificationRuleOf(event);

  return (
    <Stack spacing="4">
      <Alert status="info" rounded="md" fontSize="sm">
        <AlertIcon />
        {rule === "FOA_PART_II"
          ? t(
              "A clarification dialog is private between you and one tenderer. In Part II the dialogue may cover every side of the tenders, negotiations included (FOA § 9-3).",
            )
          : rule === "FOA_PART_III"
            ? t(
                "A clarification dialog is private between you and one tenderer. It may only clarify an existing tender, never change it (FOA § 23-5).",
              )
            : t(
                "A clarification dialog is private between you and one tenderer. It may only clarify an existing tender, never change it.",
              )}
      </Alert>

      <Box>
        <Button
          colorScheme="teal"
          leftIcon={<FiMessageSquare />}
          onClick={startModal.onOpen}
          isDisabled={!canStart}
        >
          {t("Start a clarification dialog")}
        </Button>
        {event.entriesAnonymous && (
          <Text fontSize="sm" color="smMuted" mt="2">
            {t("A dialog with a participant can start once the jury's decision is registered.")}
          </Text>
        )}
        {!dialogWindowOpen && (
          <Text fontSize="sm" color="smMuted" mt="2">
            {rule === "FOA_PART_II"
              ? t("Dialog is allowed after the submission deadline (FOA § 9-3)")
              : rule === "FOA_PART_III"
                ? t("Dialog is allowed after the submission deadline (FOA § 23-5)")
                : t("Dialog is allowed after the submission deadline")}
            {bidDeadline ? `: ${formatDateTime(bidDeadline)}` : ""}.{" "}
            {t(
              "Until then, equal treatment forbids one-to-one contact with a single tenderer — questions must be answered to all.",
            )}
          </Text>
        )}
        {dialogWindowOpen && cancelled && (
          <Text fontSize="sm" color="smMuted" mt="2">
            {t("The competition is cancelled, so no new dialog can be started.")}
          </Text>
        )}
        {dialogWindowOpen && !cancelled && counterparties.length === 0 && (
          <Text fontSize="sm" color="smMuted" mt="2">
            {t("No tenders were delivered, so there is nobody to clarify with.")}
          </Text>
        )}
      </Box>

      {clarifications.length === 0 ? (
        <Box py="6" textAlign="center" color="smMuted">
          <Text>{t("No clarification dialogs yet")}</Text>
        </Box>
      ) : (
        clarifications.map((clarification) => (
          <BuyerClarificationThread key={clarification.id} eventId={eventId} clarification={clarification} />
        ))
      )}

      {startModal.isOpen && (
        <StartClarificationModal
          eventId={eventId}
          counterparties={counterparties}
          fixedCounterpartyId={supplierOrganizationId}
          isOpen={startModal.isOpen}
          onClose={startModal.onClose}
        />
      )}
    </Stack>
  );
};
